import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './AttendanceTable.css';
import {
  ALL_TERMS,
  buildApplicability,
  buildColumns,
  buildMembership,
  computeScores,
  eventLabel,
  eventsInTerm,
  filterPeople,
  formatCount,
  formatScore,
  sortPeople,
  vacantFolders,
  withSessionsInView,
} from '../../data/selectors';
import { cellKey } from '../../data/model';
import EventMenu from './EventMenu';
import FolderMenu from './FolderMenu';
import PersonMenu from './PersonMenu';
import SortMenu from './SortMenu';

/**
 * The attendance grid.
 *
 * Header cells, sub-header cells and body cells all walk the same `columns`
 * array from `buildColumns`, so a folder, a collapsed folder and a loose event
 * are laid out by one piece of code. v1 wrote each of those three renders twice
 * — once for events inside folders and once for events outside them — and the
 * two copies drifted apart.
 */
function AttendanceTable({
  table,
  dispatch,
  filters,
  sort,
  onSortChange,
  termId = ALL_TERMS,
  readOnly,
  reveal = null,
}) {
  const [eventMenu, setEventMenu] = useState(null);
  const [folderMenu, setFolderMenu] = useState(null);
  const [personMenu, setPersonMenu] = useState(null);
  const [sortMenu, setSortMenu] = useState(null);
  const [hoverColumn, setHoverColumn] = useState(null);
  const [flashColumn, setFlashColumn] = useState(null);
  const scrollRef = useRef(null);

  const { settings } = table;

  const membership = useMemo(() => buildMembership(table.groups), [table.groups]);
  const applies = useMemo(() => buildApplicability(table), [table]);

  // Scores describe the term on screen. A semester percentage that silently
  // included last year's sessions would be worse than useless.
  const termEvents = useMemo(() => eventsInTerm(table, termId), [table, termId]);
  const scores = useMemo(() => computeScores(table, termEvents), [table, termEvents]);

  // Laid out twice: once to learn who is on screen, then again with the cohort
  // folders none of them attend folded down to a single column each.
  const scheduled = useMemo(
    () => buildColumns(table, filters.folders, termId),
    [table, filters.folders, termId]
  );

  const statusOrder = useMemo(
    () => new Map(settings.statuses.map((status, index) => [status.id, index])),
    [settings.statuses]
  );
  const statusById = useMemo(
    () => new Map(settings.statuses.map((status) => [status.id, status])),
    [settings.statuses]
  );

  const visiblePeople = useMemo(() => {
    let filtered = filterPeople(table.people, membership, filters.groups);
    // Narrowing the columns to one session narrows the roster to that session's
    // students, so picking a session is a single action rather than two.
    if (filters.onlyRelevantPeople !== false) {
      filtered = withSessionsInView(filtered, scheduled.columns, applies);
    }
    return sortPeople(filtered, sort, {
      attendance: table.attendance,
      scores,
      membership,
      statusOrder,
    });
  }, [
    table.people,
    table.attendance,
    membership,
    filters.groups,
    filters.onlyRelevantPeople,
    scheduled,
    applies,
    sort,
    scores,
    statusOrder,
  ]);

  const { headerRows, columns, depth } = useMemo(() => {
    const vacant = vacantFolders(table, scheduled.columns, visiblePeople, applies);
    return vacant.size > 0 ? buildColumns(table, filters.folders, termId, { vacant }) : scheduled;
  }, [table, filters.folders, termId, scheduled, visiblePeople, applies]);

  // Bring a session that was just added into sight. The view may still be
  // switching to show it on the first render after the add, so this waits for
  // the column to exist rather than giving up on the first look.
  const revealed = useRef(null);
  useEffect(() => {
    if (!reveal || revealed.current === reveal) return;
    // Only while the add is fresh. A column that could not be shown then (a
    // group filter hiding everyone who attends it) must not yank the grid
    // sideways minutes later, when the filter changes.
    if (Date.now() - (reveal.at || 0) > REVEAL_WINDOW_MS) return;
    const root = scrollRef.current;
    const find = (attribute, id) => id && root?.querySelector(`th[${attribute}="${cssEscape(id)}"]`);
    const target = find('data-event-id', reveal.eventId) || find('data-folder-id', reveal.folderId);
    if (!target) return;

    revealed.current = reveal;
    // A jump, not a smooth scroll: across a grid thirteen thousand pixels wide
    // an animation is slow, and it never moves at all in a window that is not
    // drawing frames. The highlight is what shows where it landed.
    target.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    setFlashColumn(reveal.eventId);
  }, [reveal, columns]);

  // Its own effect, so marking a cell while the highlight is up cannot cancel
  // the timer and leave the column lit.
  useEffect(() => {
    if (!flashColumn) return undefined;
    const timer = setTimeout(() => setFlashColumn(null), 2400);
    return () => clearTimeout(timer);
  }, [flashColumn]);

  const setStatus = useCallback(
    (personId, eventId, statusId) => dispatch({ type: 'attendance/set', personId, eventId, statusId }),
    [dispatch]
  );

  // Column highlight is one state value updated by a delegated handler. v1
  // attached onMouseEnter and onMouseLeave to every cell and stored the hovered
  // row and column, re-rendering the whole grid on each pointer move.
  const handlePointer = useCallback(
    (event) => {
      if (!settings.highlightHover) return;
      const cell = event.target.closest('td[data-column]');
      setHoverColumn(cell ? cell.dataset.column : null);
    },
    [settings.highlightHover]
  );

  // What is scheduled, not what survived folding: an emptied weekly slot is
  // not the same as nothing having been scheduled this term.
  const eventColumns = scheduled.columns.filter((column) => column.kind === 'event');
  const isEmpty = visiblePeople.length === 0 || eventColumns.length === 0;
  const hasTermFilter = termId !== ALL_TERMS && table.terms.length > 0;

  return (
    <div className="table-panel">
      <div className="table-scroll scroll-area" ref={scrollRef}>
        <table
          className={[
            'attendance-table',
            settings.stickyColumns ? 'is-sticky' : '',
            settings.highlightHover ? 'has-hover' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <thead>
            {headerRows.map((row, rowIndex) => (
              <tr key={`header-${rowIndex}`}>
                {rowIndex === 0 && (
                  <th
                    rowSpan={depth + 1}
                    className="col-name sortable"
                    onClick={() => onSortChange(nextNameSort(sort))}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      setSortMenu({ x: event.clientX, y: event.clientY });
                    }}
                    title="Click to sort by name, right-click for more options"
                  >
                    <span>Name</span>
                    <SortHint sort={sort} />
                  </th>
                )}

                {row.map((cell) =>
                  cell.kind === 'folder' ? (
                    <th
                      key={cell.key}
                      colSpan={cell.colSpan}
                      rowSpan={cell.rowSpan}
                      data-folder-id={cell.folder.id}
                      className={[
                        'col-folder',
                        cell.section ? 'col-folder--section' : '',
                        cell.vacant ? 'col-folder--vacant' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={
                        // Folding is what already happened to it; there is
                        // nothing here to expand.
                        cell.vacant ? undefined : () => dispatch({ type: 'folders/toggle', id: cell.folder.id })
                      }
                      onContextMenu={(event) => {
                        event.preventDefault();
                        // Every command in this menu is an edit.
                        if (readOnly) return;
                        setFolderMenu({ x: event.clientX, y: event.clientY, folder: cell.folder });
                      }}
                      title={
                        cell.vacant
                          ? `Nobody shown here attends ${cell.folder.name} — its ${cell.count} ${
                              cell.count === 1 ? 'date is' : 'dates are'
                            } folded away`
                          : cell.folder.isOpen
                            ? 'Collapse'
                            : 'Expand'
                      }
                    >
                      {!cell.vacant && (
                        <span className="folder-toggle">{cell.folder.isOpen ? '▼' : '▶'}</span>
                      )}
                      {cell.folder.name}
                      {cell.folder.groupId && (
                        <CohortBadge folder={cell.folder} groups={table.groups} />
                      )}
                      {(cell.collapsed || cell.vacant) && cell.count > 0 && (
                        <span className="col-folder__count">{cell.count}</span>
                      )}
                    </th>
                  ) : cell.kind === 'event' ? (
                    <EventHeader
                      key={cell.key}
                      event={cell.event}
                      folder={cell.folder}
                      rowSpan={cell.rowSpan}
                      sort={sort}
                      flash={flashColumn === cell.event.id}
                      onSort={() => onSortChange(nextEventSort(sort, cell.event.id))}
                      onMenu={readOnly ? null : (x, y) => setEventMenu({ x, y, event: cell.event })}
                    />
                  ) : (
                    <th
                      key={cell.key}
                      colSpan={cell.colSpan}
                      rowSpan={cell.rowSpan}
                      className="col-folder col-folder--spacer"
                      aria-hidden="true"
                    />
                  )
                )}

                {rowIndex === 0 && (
                  <>
                    {/* The old sheets kept this beside the percentage, and it
                        answers a different question: how many sessions someone
                        actually made. */}
                    <th
                      rowSpan={depth + 1}
                      className="col-score"
                      title="Sessions attended out of those that counted"
                    >
                      Present
                    </th>
                    <th
                      rowSpan={depth + 1}
                      className="col-score sortable"
                      onClick={() => onSortChange(nextScoreSort(sort, 'raw'))}
                      title="Share of counted events attended"
                    >
                      Raw
                      <ScoreHint sort={sort} scoreType="raw" />
                    </th>
                    <th
                      rowSpan={depth + 1}
                      className="col-score sortable"
                      onClick={() => onSortChange(nextScoreSort(sort, 'weighted'))}
                      title="Same, but each event counts for its weight"
                    >
                      Weighted
                      <ScoreHint sort={sort} scoreType="weighted" />
                    </th>
                  </>
                )}
              </tr>
            ))}
          </thead>

          <tbody onMouseOver={handlePointer} onMouseLeave={() => setHoverColumn(null)}>
            {visiblePeople.map((person) => {
              const score = scores.get(person.id);
              const personGroups = membership.get(person.id) || [];
              return (
                <tr key={person.id}>
                  <td
                    className="col-name"
                    title={
                      personGroups.length > 0
                        ? `${person.name} — ${personGroups.map((group) => group.name).join(', ')}`
                        : person.name
                    }
                    onContextMenu={(event) => {
                      event.preventDefault();
                      if (readOnly) return;
                      setPersonMenu({ x: event.clientX, y: event.clientY, person });
                    }}
                  >
                    <div className="name-cell">
                      <span className="group-bars">
                        {personGroups.map((group) => (
                          <span
                            key={group.id}
                            className="group-bars__bar"
                            style={{ background: group.color }}
                            title={group.name}
                          />
                        ))}
                      </span>
                      <span className="person-name">{person.name}</span>
                    </div>
                  </td>

                  {columns.map((column) => {
                    if (column.kind !== 'event') {
                      return <td key={column.id} className="col-collapsed" aria-hidden="true" />;
                    }
                    // Not this person's session: no control, nothing to mark by
                    // accident, and nothing counted either way.
                    if (!applies(person.id, column.event)) {
                      return (
                        <td
                          key={column.id}
                          className={`col-event col-event--n-a${
                            hoverColumn === column.id || flashColumn === column.id ? ' is-hover-column' : ''
                          }`}
                          data-column={column.id}
                          title={`${person.name} is not in ${column.folder?.name || 'this session'}`}
                        />
                      );
                    }

                    const statusId = table.attendance[cellKey(person.id, column.event.id)] || '';
                    return (
                      <AttendanceCell
                        key={column.id}
                        columnId={column.id}
                        personId={person.id}
                        eventId={column.event.id}
                        eventName={column.event.name}
                        personName={person.name}
                        statusId={statusId}
                        statuses={settings.statuses}
                        statusById={statusById}
                        colorCells={settings.colorCells}
                        colorDropdown={settings.colorDropdown}
                        highlighted={hoverColumn === column.id || flashColumn === column.id}
                        readOnly={readOnly}
                        onChange={setStatus}
                      />
                    );
                  })}

                  <td className="col-score col-score--count">{formatCount(score)}</td>
                  <td className="col-score">{formatScore(score?.raw)}</td>
                  <td className="col-score">{formatScore(score?.weighted)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {isEmpty && (
          <p className="table-empty">
            {table.people.length === 0
              ? 'No people yet — use Add → People to paste in your roster.'
              : eventColumns.length === 0 && hasTermFilter && table.events.length > 0
                ? 'Nothing scheduled in this term yet. Use Add → Weekly session, or switch to All terms.'
                : eventColumns.length === 0
                  ? 'No sessions yet — use Add → Weekly session to build out a term.'
                  : 'Nobody attends the sessions in view. Clear the filter, or add people to this group.'}
          </p>
        )}
      </div>

      {eventMenu && (
        <EventMenu
          {...eventMenu}
          table={table}
          dispatch={dispatch}
          visiblePeople={visiblePeople}
          applies={applies}
          onClose={() => setEventMenu(null)}
        />
      )}
      {folderMenu && (
        <FolderMenu
          {...folderMenu}
          folders={table.folders}
          groups={table.groups}
          dispatch={dispatch}
          onClose={() => setFolderMenu(null)}
        />
      )}
      {personMenu && (
        <PersonMenu
          {...personMenu}
          people={table.people}
          dispatch={dispatch}
          onClose={() => setPersonMenu(null)}
        />
      )}
      {sortMenu && (
        <SortMenu
          {...sortMenu}
          sort={sort}
          onSortChange={onSortChange}
          onClose={() => setSortMenu(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* cells                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Memoised so that marking one cell re-renders that cell and not the other few
 * hundred. Everything it receives is a primitive or a stable reference.
 */
const AttendanceCell = memo(function AttendanceCell({
  columnId,
  personId,
  eventId,
  personName,
  eventName,
  statusId,
  statuses,
  statusById,
  colorCells,
  colorDropdown,
  highlighted,
  readOnly,
  onChange,
}) {
  const status = statusById.get(statusId);
  const background = colorCells && status ? status.color : undefined;

  return (
    <td className={`col-event${highlighted ? ' is-hover-column' : ''}`} data-column={columnId}>
      <select
        className="attendance-select"
        value={statusId}
        disabled={readOnly}
        style={{ background }}
        aria-label={`${personName} — ${eventName}`}
        onChange={(event) => onChange(personId, eventId, event.target.value || null)}
      >
        <option value="">—</option>
        {statuses.map((option) => (
          <option
            key={option.id}
            value={option.id}
            style={colorDropdown ? { background: option.color } : undefined}
          >
            {option.name}
          </option>
        ))}
      </select>
    </td>
  );
});

const REVEAL_WINDOW_MS = 3000;

const cssEscape = (value) =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');

/**
 * Who attends a folder's sessions. The group is named whenever it is not the
 * folder's own name: a "Friday 2pm" folder attended by the Friday 11am group
 * is the mistake worth seeing, and a bare member count hid it.
 */
function CohortBadge({ folder, groups }) {
  const group = groups.find((entry) => entry.id === folder.groupId);
  if (!group) return null;
  const sameName = group.name.trim().toLowerCase() === folder.name.trim().toLowerCase();

  return (
    <span
      className={`col-folder__cohort${sameName ? '' : ' col-folder__cohort--named'}`}
      title={`Only ${group.name} (${group.memberIds.length}) attend these sessions`}
    >
      {sameName ? group.memberIds.length : `${group.name} · ${group.memberIds.length}`}
    </span>
  );
}

function EventHeader({ event, folder, rowSpan, sort, flash, onSort, onMenu }) {
  const active = sort.type === 'event' && sort.eventId === event.id;
  const label = eventLabel(event, folder);

  return (
    <th
      rowSpan={rowSpan}
      data-event-id={event.id}
      className={`col-event sortable${active ? ' is-sorted' : ''}${flash ? ' is-revealed' : ''}`}
      onClick={onSort}
      onContextMenu={(domEvent) => {
        if (!onMenu) return;
        domEvent.preventDefault();
        onMenu(domEvent.clientX, domEvent.clientY);
      }}
      title={`${event.name || label.name} — click to sort, right-click to edit`}
    >
      <span className="col-event__name">{label.name}</span>
      <span className="col-event__meta">
        {label.meta}
        {/* Weight is noise when everything counts the same. */}
        {event.weight !== 1 && <span className="col-event__weight">×{event.weight}</span>}
      </span>
    </th>
  );
}

/* -------------------------------------------------------------------------- */
/* sort transitions                                                           */
/* -------------------------------------------------------------------------- */

const ASC = 'asc';
const DESC = 'desc';

/** Name header cycles A→Z, Z→A, off. */
export function nextNameSort(sort) {
  if (sort.type !== 'firstName') return { ...sort, type: 'firstName', direction: ASC };
  if (sort.direction === ASC) return { ...sort, direction: DESC };
  return { type: 'none', direction: ASC, eventId: null, scoreType: null };
}

export function nextEventSort(sort, eventId) {
  if (sort.type !== 'event' || sort.eventId !== eventId) {
    return { type: 'event', direction: ASC, eventId, scoreType: null };
  }
  // v1 had no reverse here at all: clicking an event twice just switched sorting off.
  if (sort.direction === ASC) return { ...sort, direction: DESC };
  return { type: 'none', direction: ASC, eventId: null, scoreType: null };
}

export function nextScoreSort(sort, scoreType) {
  if (sort.type !== 'score' || sort.scoreType !== scoreType) {
    // Lowest attendance first is what anyone opening this column is looking for.
    return { type: 'score', direction: ASC, eventId: null, scoreType };
  }
  if (sort.direction === ASC) return { ...sort, direction: DESC };
  return { type: 'none', direction: ASC, eventId: null, scoreType: null };
}

const ARROW = { asc: '↑', desc: '↓' };

function SortHint({ sort }) {
  if (!['firstName', 'lastName', 'group'].includes(sort.type)) return null;
  const label = { firstName: 'first', lastName: 'last', group: 'group' }[sort.type];
  return <span className="sort-hint">{label} {ARROW[sort.direction]}</span>;
}

function ScoreHint({ sort, scoreType }) {
  if (sort.type !== 'score' || sort.scoreType !== scoreType) return null;
  return <span className="sort-hint">{ARROW[sort.direction]}</span>;
}

export default AttendanceTable;
